function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { CognitoIdentityProviderClient, InitiateAuthCommand } from '@aws-sdk/client-cognito-identity-provider';
import axiosStatic, { AxiosHeaders } from 'axios';
import { HttpClient } from 'api/http-client';
import RocketlaneLogo from 'assets/rocketlane_logo_64px.png';
import { API_HOST_FOR_MOBILE_META, API_NAMESPACE, API_VERSION, updateApiHostForCookies } from 'config/environment';
import { AWS_COGNITO_CLIENT_REGION } from 'constant/constant';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';
const customDomainApi = '/accounts/custom-domain';
export var TimesheetAccessState = /*#__PURE__*/ function(TimesheetAccessState) {
    TimesheetAccessState["Enabled"] = "ENABLED";
    TimesheetAccessState["OnlyApproval"] = "ONLY_APPROVAL";
    TimesheetAccessState["Disabled"] = "DISABLED";
    return TimesheetAccessState;
}({});
export var AccountStatus = /*#__PURE__*/ function(AccountStatus) {
    AccountStatus["Trial"] = "TRIAL";
    AccountStatus["Active"] = "ACTIVE";
    AccountStatus["Grace"] = "GRACE";
    AccountStatus["Suspended"] = "SUSPENDED";
    AccountStatus["Paid"] = "PAID";
    return AccountStatus;
}({});
export var WarningType = /*#__PURE__*/ function(WarningType) {
    WarningType["Billing"] = "BILLING";
    WarningType["Nitro"] = "NITRO";
    return WarningType;
}({});
export var IdpType = /*#__PURE__*/ function(IdpType) {
    IdpType["Google"] = "GOOGLE";
    IdpType["Saml"] = "SAML";
    return IdpType;
}({});
export var AuthenticationServiceProvider = /*#__PURE__*/ function(AuthenticationServiceProvider) {
    AuthenticationServiceProvider["COGNITO"] = "COGNITO";
    AuthenticationServiceProvider["OKTA"] = "OKTA";
    return AuthenticationServiceProvider;
}({});
export var RequestPlanType = /*#__PURE__*/ function(RequestPlanType) {
    RequestPlanType["Buy"] = "BUY";
    RequestPlanType["TrialExtend"] = "TRIAL_EXTEND";
    return RequestPlanType;
}({});
class AccountHttpClient extends HttpClient {
    async getAccountDetails() {
        return this.get('/accounts/profile');
    }
    async updateCurrencyMapping(payload) {
        return this.put(`/accounts/currency-mapping${payload.backdate != null ? `?backdate=${payload.backdate}` : ''}`, payload);
    }
    async updateAccount(data) {
        return this.put('/accounts/profile', data);
    }
    async updateOutOfOffice(data) {
        return this.put('/accounts/profile/out-of-office', data);
    }
    async updateTimezone(data) {
        return this.put('/accounts/profile', data);
    }
    async updateCalendarConfig(payload) {
        return this.post('/accounts/calendar-config', payload);
    }
    async getMeta(domain, pathname) {
        var _response_features;
        var _response_okta;
        // if in localstorage, return that
        // else fetch from api and store in localstorage
        if (localStorage.getItem('accountMeta')) {
            return new Promise((resolve)=>{
                const meta = localStorage.getItem('accountMeta');
                if (meta) {
                    var _parsed_okta;
                    const parsed = JSON.parse(meta);
                    if (parsed === null || parsed === void 0 ? void 0 : (_parsed_okta = parsed.okta) === null || _parsed_okta === void 0 ? void 0 : _parsed_okta.cookiesEnabled) {
                        updateApiHostForCookies();
                    }
                    resolve(parsed);
                }
            });
        }
        if (navigator.userAgent.includes('Mobile') && localStorage.getItem('customer_portal') !== 'true' && !((pathname === null || pathname === void 0 ? void 0 : pathname.startsWith('/forms')) || (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith('/cp')) || (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith('/customer-portal')))) {
            return axiosStatic.get(`${API_HOST_FOR_MOBILE_META}/${API_NAMESPACE}/${API_VERSION}/accounts/get-mobile-meta?domain=${domain}`, {
                headers: new AxiosHeaders({})
            }).then((x)=>x.data).then((response)=>{
                var _response_features;
                const { company } = response;
                const { companyLogoUrl } = company;
                return {
                    ...response,
                    company: {
                        ...company,
                        companyLogoUrl
                    },
                    features: (_response_features = response.features) !== null && _response_features !== void 0 ? _response_features : {}
                };
            });
        }
        let response;
        const makeManualCall = async ()=>{
            return this.get('/accounts/get-meta', {
                headers: new AxiosHeaders({})
            });
        };
        if (process.env.NODE_ENV === 'test') {
            response = await makeManualCall();
            return response;
        }
        try {
            response = await InitWorkerApi.getData(InitWorkerRequestType.GetMeta);
        } catch (unused) {
            response = await makeManualCall();
        }
        // On every successful get-meta, update host when cookiesEnabled
        if (response === null || response === void 0 ? void 0 : (_response_okta = response.okta) === null || _response_okta === void 0 ? void 0 : _response_okta.cookiesEnabled) {
            updateApiHostForCookies();
        }
        const { company } = response;
        const companyLogoUrl = company.companyLogoUrl || RocketlaneLogo;
        const data = {
            ...response,
            company: {
                ...company,
                companyLogoUrl
            },
            features: (_response_features = response.features) !== null && _response_features !== void 0 ? _response_features : {}
        };
        return data;
    }
    async getEmailConfig() {
        return this.get('/email/config');
    }
    async addEmailConfig(data) {
        return this.post('/email/config', data);
    }
    async updateEmailConfig(data) {
        return this.put('/email/config', data);
    }
    async verifyEmailConfig(data) {
        return this.post('/email/verify', data);
    }
    async toggleFTUE(payload) {
        return this.put('/accounts/ftue', payload);
    }
    constructor(...args){
        super(...args), _define_property(this, "getCurrencyList", async ()=>this.get('/accounts/currencies')), _define_property(this, "getCurrencyMappingList", async ()=>this.get('/accounts/currency-mapping')), _define_property(this, "addCurrencyMapping", async (payload)=>this.post('/accounts/currency-mapping', payload)), _define_property(this, "getCurrencyMappingHistory", async (currencyCode)=>this.get(`/accounts/currency-mapping/history/${currencyCode}`)), _define_property(this, "getCurrencyMappingAuditLog", async (currencyCode)=>this.get(`/accounts/currency-mapping/audit-log/${currencyCode}`)), _define_property(this, "getCustomDomain", ()=>this.get(customDomainApi)), _define_property(this, "createCustomDomain", (payload)=>this.post(customDomainApi, payload)), _define_property(this, "verifyCustomDomain", (payload)=>this.post(`${customDomainApi}/verify`, payload)), _define_property(this, "getInitData", async ()=>{
            var _data_userProfile_permissions_projectPermissionBeanMap;
            let data;
            const makeManualCall = async ()=>{
                return this.get('/home/init');
            };
            if (process.env.NODE_ENV === 'test') {
                data = await makeManualCall();
                return data;
            }
            try {
                data = await InitWorkerApi.getData(InitWorkerRequestType.Init);
            } catch (unused) {
                data = await makeManualCall();
            }
            return {
                ...data,
                userProfile: {
                    ...data.userProfile,
                    permissions: {
                        ...data.userProfile.permissions,
                        projects: (_data_userProfile_permissions_projectPermissionBeanMap = data.userProfile.permissions.projectPermissionBeanMap) !== null && _data_userProfile_permissions_projectPermissionBeanMap !== void 0 ? _data_userProfile_permissions_projectPermissionBeanMap : []
                    }
                }
            };
        }), _define_property(this, "getConfigData", async ()=>{
            let data;
            const makeManualCall = async ()=>{
                return this.get('/home/configuration');
            };
            if (process.env.NODE_ENV === 'test') {
                data = await makeManualCall();
                return data;
            }
            try {
                data = await InitWorkerApi.getData(InitWorkerRequestType.AccountConfig);
            } catch (unused) {
                data = await makeManualCall();
            }
            return data;
        }), _define_property(this, "signUp", ({ token, ...payload })=>{
            return this.put('/accounts/sign-up', payload, {
                params: {
                    token
                }
            });
        }), _define_property(this, "resendAccountActivation", ()=>{
            return this.post('/accounts/activate/resend');
        }), _define_property(this, "isDomainExists", ({ token, ...payload })=>{
            if (payload.throughGoogleAuth) {
                return this.post('https://admin.api.rocketlane.com/api/v1/accounts/domain', {
                    domainName: payload.domainName
                }, {
                    params: {
                        token
                    }
                });
            }
            return this.post('/accounts/domain', {
                domainName: payload.domainName
            }, {
                params: {
                    token
                }
            });
        }), _define_property(this, "requestPlan", (payload)=>{
            return this.put('/accounts/request-plan', payload);
        }), _define_property(this, "forgotAccount", (payload)=>{
            return this.post('https://admin.api.rocketlane.com/api/v1/accounts/forgot', {
                emailId: payload.email
            });
        }), _define_property(this, "signUpThroughGoogle", ({ ...payload })=>{
            return this.post('https://admin.api.rocketlane.com/api/v1/accounts/signup-google', payload);
        }), _define_property(this, "memberActivateResend", ({ email })=>{
            return this.post('/members/activate/resend', {
                email
            });
        }), // eslint-disable-next-line class-methods-use-this
        _define_property(this, "loginWithCognitoSDK", async (payload)=>{
            // Extract region from issuerDomain if available, otherwise fallback to default
            // issuerDomain format: https://<domain>.auth.<region>.amazoncognito.com
            let region = AWS_COGNITO_CLIENT_REGION; // default fallback
            if (payload.issuerDomain) {
                const regionMatch = payload.issuerDomain.match(/\.auth\.([^.]+)\.amazoncognito\.com/);
                if (regionMatch) {
                    const [, matchedRegion] = regionMatch;
                    if (matchedRegion) {
                        region = matchedRegion;
                    }
                }
            }
            const awsCognitoClient = new CognitoIdentityProviderClient({
                region
            });
            const userName = payload.userName.trim() + payload.userNamesuffix;
            const input = {
                AuthFlow: 'USER_PASSWORD_AUTH',
                ClientId: payload.clientId,
                AuthParameters: {
                    USERNAME: userName,
                    PASSWORD: payload.password
                }
            };
            const command = new InitiateAuthCommand(input);
            const { AuthenticationResult } = await awsCognitoClient.send(command);
            if (AuthenticationResult != null) {
                const { AccessToken } = AuthenticationResult;
                return {
                    token: AccessToken
                };
            }
            return {
                token: null
            };
        }), _define_property(this, "loginWithCognitoCustomEndpoint", async (payload)=>{
            const userName = payload.userName.trim() + payload.userNamesuffix;
            const response = await this.post('authentication/sign-in', {
                userName,
                password: payload.password,
                clientId: payload.clientId
            });
            return response;
        }), _define_property(this, "loginWithCognito", async (payload, isCustomAuthentication)=>{
            if (isCustomAuthentication) {
                return this.loginWithCognitoCustomEndpoint(payload);
            }
            return this.loginWithCognitoSDK(payload);
        }), // eslint-disable-next-line class-methods-use-this
        _define_property(this, "loginWithCognitoSSO", async (issuerDomain, code, redirectURI, codeVerifier, clientId)=>{
            // Only include code_verifier when present. URLSearchParams serialises a JS `undefined` as the
            // literal string 'undefined', which Cognito's PKCE check rejects with HTTP 400 — the root of
            // the "code_verifier=undefined" / "Something went wrong" login failures.
            const tokenParams = {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                grant_type: 'authorization_code',
                code,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                redirect_uri: redirectURI,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                client_id: clientId
            };
            if (codeVerifier) {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                tokenParams.code_verifier = codeVerifier;
            }
            const queryParams = new URLSearchParams(tokenParams);
            try {
                const response = await axiosStatic.post(`${issuerDomain}/oauth2/token?${queryParams.toString()}`, {}, {
                    headers: {
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                });
                return response.data;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                const ssoError = new Error(`Login with Cognito SSO failed: ${errorMessage}`);
                ssoError.cause = error;
                throw ssoError;
            }
        });
    }
}
export const AccountAPI = new AccountHttpClient();
